import { useId } from "react";

type Point = { t: number; price: number };

const fmtAxis = (v: number) =>
  v >= 1000 ? `$${Math.round(v).toLocaleString()}` : v >= 1 ? `$${v.toFixed(2)}` : `$${v.toFixed(4)}`;

const fmtHour = (unixSec: number) => {
  const d = new Date(unixSec * 1000);
  const h = d.getUTCHours();
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr} ${h < 12 ? "AM" : "PM"}`;
};

const fmtDay = (unixSec: number) => {
  const d = new Date(unixSec * 1000);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

export const AreaChart = ({ points, up, height = 96, xMode = "hour", ariaLabel, testid }: {
  points: Point[]; up: boolean; height?: number; xMode?: "hour" | "day"; ariaLabel: string; testid?: string;
}) => {
  const gid = useId().replace(/:/g, "");
  if (points.length < 2) return null;
  const prices = points.map((p) => p.price);
  const min = Math.min(...prices), max = Math.max(...prices);
  const pad = (max - min) * 0.08 || max * 0.005 || 1;
  const lo = min - pad, hi = max + pad;
  const W = 100, H = 100;
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(2)},${y(p.price).toFixed(2)}`).join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  const color = up ? "#22C55E" : "#EF4444";
  const fmtX = xMode === "hour" ? fmtHour : fmtDay;
  const xIdx = [0, Math.floor((points.length - 1) / 3), Math.floor(((points.length - 1) * 2) / 3), points.length - 1];

  return (
    <div data-testid={testid} role="img" aria-label={ariaLabel}>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between py-0.5 text-right shrink-0" aria-hidden>
          <span className="font-mono text-[9px] text-gray-600 leading-none">{fmtAxis(max)}</span>
          <span className="font-mono text-[9px] text-gray-600 leading-none">{fmtAxis((max + min) / 2)}</span>
          <span className="font-mono text-[9px] text-gray-600 leading-none">{fmtAxis(min)}</span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="flex-1 min-w-0" style={{ height }} aria-hidden>
          <defs>
            <linearGradient id={`ac-${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {[25, 50, 75].map((gy) => (
            <line key={gy} x1="0" x2={W} y1={gy} y2={gy} stroke="rgba(255,255,255,0.05)" strokeWidth="0.4" />
          ))}
          <path d={area} fill={`url(#ac-${gid})`} />
          <path d={line} fill="none" stroke={color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="mt-1 flex justify-between pl-9" aria-hidden>
        {xIdx.map((i, n) => (
          <span key={n} className="font-mono text-[9px] text-gray-600">{fmtX(points[i].t)}</span>
        ))}
      </div>
    </div>
  );
};
